import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Button, Tabs } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import CategoryView from "./CategoryView";
import PeopleView from "./PeopleView";
import ServiceTracker from "./ServiceTracker";
import TicketsAndQueries from "./TicketsAndQueries";
import CategoryDetailPage from "./CategoryDetailPage";
import COLORS from "common/Colors";
import { ButtonOutlined } from "reusableComponent/UIButtons";

/* ─── Styled ─────────────────────────────────────────────── */
const Wrapper = styled.div`
  background: #fff;
  font-family: "Inter", sans-serif;
  .ant-input-affix-wrapper,
  .ant-input,
  .ant-btn {
    border-radius: 24px;
  }

  .ant-select .ant-select-selector {
    border-radius: 24px;
  }

  .ant-input::placeholder {
    color: #000;
  }

  .ant-input:hover::placeholder {
    color: "#2d53da";
  }
  .ant-input::placeholder,
  .ant-picker-input > input::placeholder {
    color: #9ca3af !important;
  }

  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }

  .ant-input,
  .ant-picker,
  .ant-select-selector {
    border-radius: 24px !important;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
`;

/* Match the existing TabsList visual style */
const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    padding-left: 10px;
    padding-right: 10px;
    border-radius: 5px;
    font-weight: 500;
    color: ${COLORS.TEXT.PRIMARY};
  }
  .ant-tabs-tab {
    font-size: 16px;
    /* padding-right: 20px; */
    &:hover {
      color: ${COLORS.TEXT.PRIMARY};
    }
  }
  .ant-tabs-ink-bar {
    background: ${COLORS.TAB};
    font-weight: bold;
    position: absolute;
    bottom: -1px !important;
  }
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${COLORS.TAB};
    font-weight: 500;
    font-size: 16px;
  }

  .ant-tabs-nav::before {
    border: none !important;
  }
  .ant-tabs-nav-list {
    border-bottom: 2px solid #80808033;
  }

  @media (min-width: 320px) and (max-width: 420px) {
    .ant-tabs-tab {
      margin: 0 !important;
    }
    .ant-tabs-nav .ant-tabs-nav-list {
      gap: 1rem;
    }
  }
`;

/* ─── Component ──────────────────────────────────────────── */
const AssetManagement = () => {
  const [drillCategory, setDrillCategory] = useState(null);

  // activeTab is the single source of truth — set on every tab click
  const [activeTab, setActiveTab] = useState("category");

  // One ref per tab — each tab registers its own export function here.
  // Refs are stable objects; they are never overwritten by other tabs.
  const categoryExportRef = useRef(null);
  const peopleExportRef = useRef(null);
  const serviceExportRef = useRef(null);
  const ticketsExportRef = useRef(null);

  const refMap = {
    category: categoryExportRef,
    people: peopleExportRef,
    service: serviceExportRef,
    tickets: ticketsExportRef,
  };

  const handleExportCSV = () => {
    const fn = refMap[activeTab]?.current;
    if (typeof fn === "function") {
      fn();
    }
  };

  const TAB_ITEMS = [
    {
      key: "category",
      label: "Category View",
      children: (
        <CategoryView
          setDrillCategory={setDrillCategory}
          exportRef={categoryExportRef}
        />
      ),
    },
    {
      key: "people",
      label: "People View",
      children: <PeopleView exportRef={peopleExportRef} />,
    },
    {
      key: "service",
      label: "Service Tracker",
      children: <ServiceTracker exportRef={serviceExportRef} />,
    },
    {
      key: "tickets",
      label: "Tickets & Queries",
      children: <TicketsAndQueries exportRef={ticketsExportRef} />,
    },
  ];

  return (
    <Wrapper>
      {drillCategory !== null && (
        <CategoryDetailPage
          category={drillCategory}
          onBack={() => setDrillCategory(null)}
        />
      )}

      {drillCategory === null && (
        <>
          <Header>
            <Title>Asset Management</Title>
            <ButtonOutlined
              icon={<DownloadOutlined />}
              style={{ borderRadius: 6 }}
              onClick={handleExportCSV}
            >
              Export CSV File
            </ButtonOutlined>
          </Header>

          <StyledTabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)} // fires reliably on every tab click
            items={TAB_ITEMS}
            destroyInactiveTabPane={false} // keep tab components mounted so refs stay alive
          />
        </>
      )}
    </Wrapper>
  );
};

export default AssetManagement;
